import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ExchangeRateModal,
  ExchangeRateTable,
} from "../../components/backend/exchangerates";
import {
  useExchangeRateMutations,
  useExchangeRates,
} from "../../hooks/useExchangeRateAdmin";
import { ExchangeRate } from "../../types/admin";

import AdminPage from "./AdminPage";

type ModalState = {
  open: boolean;
  mode: "create" | "edit";
  rate?: ExchangeRate;
};

const ExchangeRatePage: React.FC = () => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState<ExchangeRate | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    mode: "create",
  });

  const { data, isLoading } = useExchangeRates();
  const { create, update, remove } = useExchangeRateMutations();

  const rates = useMemo(() => data?.data ?? [], [data]);

  const openCreateModal = () =>
    setModalState({ open: true, mode: "create", rate: undefined });

  const openEditModal = (rate: ExchangeRate) =>
    setModalState({ open: true, mode: "edit", rate });

  const closeModal = () =>
    setModalState((prev) => ({ ...prev, open: false, rate: undefined }));

  const showMessage = (message: string, severity: AlertColor = "info") =>
    setSnackbar({ open: true, message, severity });

  const handleSave = async (payload: {
    sellValue: number;
    buyValue: number;
    isActive: boolean;
    comment: string;
  }) => {
    try {
      if (modalState.mode === "create") {
        await create.mutateAsync(payload);
        showMessage(t("admin.exchangeRates.createSuccess"), "success");
      } else if (modalState.rate?.id) {
        await update.mutateAsync({ id: modalState.rate.id, data: payload });
        showMessage(t("admin.exchangeRates.updateSuccess"), "success");
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save exchange rate", error);
      showMessage(t("admin.exchangeRates.saveError"), "error");
    }
  };

  const handleDelete = async (rate: ExchangeRate) => {
    setConfirmDelete(rate);
  };

  const confirmDeletion = async () => {
    if (!confirmDelete) return;
    try {
      await remove.mutateAsync(confirmDelete.id);
      showMessage(t("admin.exchangeRates.deleteSuccess"), "success");
    } catch (error) {
      console.error("Failed to delete exchange rate", error);
      showMessage(t("admin.exchangeRates.deleteError"), "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("admin.exchangeRates.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.exchangeRates.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            {t("admin.exchangeRates.add")}
          </Button>
        </Box>

        <ExchangeRateTable
          rates={rates}
          loading={
            isLoading ||
            create.isPending ||
            update.isPending ||
            remove.isPending
          }
          onEdit={openEditModal}
          onDelete={handleDelete}
        />

        <ExchangeRateModal
          open={modalState.open}
          mode={modalState.mode}
          initialData={modalState.rate}
          onClose={closeModal}
          onSave={handleSave}
          submitting={create.isPending || update.isPending}
        />

        <Dialog
          open={Boolean(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("admin.exchangeRates.deleteTitle")}</DialogTitle>
          <DialogContent>
            {t("admin.exchangeRates.deleteConfirmation")}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>{t("admin.exchangeRates.cancel")}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeletion}
              disabled={remove.isPending}
            >
              {t("admin.exchangeRates.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminPage>
  );
};

export default ExchangeRatePage;

